import React, { useState, useMemo } from 'react';
import Icon from '../../Icon';
import { C } from '../../../utils/styles';
import StyledSelect from '../../StyledSelect';
import { previewInferredFilingDistribution, applyInferredFiling } from '../../../utils/deterministicFilingInference';

// Renders sequential prompts for missing fields (filing, state, payFreq).
// Calls onResolve({ employees, choices }) once the user has answered every
// prompt that applies.

const FILING_STRATEGY_OPTIONS = [
  {
    value: 'distribution',
    title: 'Use realistic distribution (recommended)',
    desc: 'Apply industry-standard distribution: 50% Single / 35% Married / 15% Head of Household. Aggregate numbers are accurate; individual employees may not match their real status — they are flagged as inferred.',
  },
  {
    value: 'all_single',
    title: 'Assume all Single',
    desc: 'Fastest, but may overstate eligibility by ~15-20% for workforces with married employees.',
  },
  {
    value: 'aggregate',
    title: "I have aggregate filing status data",
    desc: 'Provide your known breakdown (in %).',
  },
];

function FilingStatusPrompt({ employees, clientId, onResolve, onSkip }) {
  const [strategy, setStrategy] = useState('distribution');
  const [single, setSingle] = useState(50);
  const [married, setMarried] = useState(35);
  const head = Math.max(0, 100 - single - married);

  const missingCount = employees.filter((e) => !e.filing).length;

  const distribution = useMemo(() => {
    if (strategy === 'aggregate') {
      return { single: single / 100, married: married / 100, head: head / 100 };
    }
    return { single: 0.5, married: 0.35, head: 0.15 };
  }, [strategy, single, married, head]);

  const preview = useMemo(() => {
    const opts = { clientId, strategy, distribution, aggregateOverride: distribution };
    return previewInferredFilingDistribution(employees, opts);
  }, [employees, clientId, strategy, distribution]);

  return (
    <div data-testid="smart-upload-missing-filing" style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'start', gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, background: C.orangeLight,
          color: C.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name="warning-outline" style={{ fontSize: 20 }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
            Filing status missing for {missingCount} of {employees.length} employees
          </div>
          <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.5 }}>
            Filing status is the single biggest factor in Section 125 eligibility — assuming the wrong status can overstate eligibility by 15-20%. Choose how you'd like us to handle it.
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {FILING_STRATEGY_OPTIONS.map((opt) => {
          const active = strategy === opt.value;
          return (
            <label
              key={opt.value}
              data-testid={`filing-strategy-${opt.value}`}
              style={{
                display: 'block',
                padding: 14,
                borderRadius: 10,
                border: `1px solid ${active ? C.blue : C.border}`,
                background: active ? C.blueLight : '#fff',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                  type="radio"
                  name="filing-strategy"
                  value={opt.value}
                  checked={active}
                  onChange={() => setStrategy(opt.value)}
                  style={{ width: 16, height: 16 }}
                />
                <div style={{ fontSize: 14, fontWeight: 700 }}>{opt.title}</div>
              </div>
              <div style={{ fontSize: 12, color: C.textMid, marginTop: 6, marginLeft: 26, lineHeight: 1.5 }}>
                {opt.desc}
              </div>
              {active && opt.value === 'aggregate' && (
                <div style={{
                  marginTop: 10, marginLeft: 26,
                  display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10,
                }}>
                  <NumberField label="Single %" value={single} onChange={setSingle} />
                  <NumberField label="Married %" value={married} onChange={setMarried} />
                  <NumberField label="HoH %" value={head} onChange={() => {}} disabled />
                </div>
              )}
            </label>
          );
        })}
      </div>

      <div style={{
        marginTop: 16, padding: 12, borderRadius: 8, background: C.bg,
        fontSize: 13, color: C.textMid, lineHeight: 1.5,
      }}>
        <div style={{ fontWeight: 700, color: C.text, marginBottom: 4 }}>Preview</div>
        Single: <strong>{preview.single}</strong> · Married: <strong>{preview.married}</strong> · HoH: <strong>{preview.head}</strong>
        <div style={{ marginTop: 4, fontSize: 12, color: C.textLight }}>
          Each inferred employee will be flagged so you can replace with real data later — and the deterministic seed means the same employee always lands in the same bucket on every re-run.
        </div>
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        {onSkip && (
          <button
            onClick={onSkip}
            style={{
              padding: '10px 16px', background: 'transparent', color: C.textMid,
              border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Skip for now
          </button>
        )}
        <button
          data-testid="filing-strategy-apply"
          onClick={() => {
            const updated = applyInferredFiling(employees, {
              clientId, strategy, distribution, aggregateOverride: distribution,
            });
            onResolve(updated, { strategy, distribution });
          }}
          style={{
            padding: '10px 20px', background: C.blue, color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, disabled }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 11, color: C.textMid, marginBottom: 4 }}>{label}</div>
      <input
        type="number"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
        style={{
          width: '100%', padding: '8px 10px',
          border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13,
        }}
      />
    </label>
  );
}

function StatePrompt({ employees, onResolve }) {
  const [state, setState] = useState('CA');
  const stateOptions = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
    .map((s) => ({ value: s, label: s }));
  return (
    <div data-testid="smart-upload-missing-state" style={{
      background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <Icon name="location-outline" style={{ fontSize: 18, color: C.blue }} />
        <div style={{ fontSize: 16, fontWeight: 700 }}>State missing</div>
      </div>
      <div style={{ fontSize: 13, color: C.textMid, marginBottom: 12 }}>
        Your file doesn't include state. Set the state for all {employees.length} employees:
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ width: 200 }}>
          <StyledSelect value={state} onValueChange={setState} options={stateOptions} />
        </div>
        <button
          data-testid="state-prompt-apply"
          onClick={() => onResolve(employees.map((e) => ({ ...e, state })))}
          style={{
            padding: '10px 20px', background: C.blue, color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
          }}
        >
          Apply state
        </button>
      </div>
    </div>
  );
}

function LocationPrompt({ employees, onResolve, onSkip }) {
  // Trigger only if no employee has a city/zip AND there's at least one US state present.
  const missing = employees.filter((e) => !e.city && !e.zip).length;
  const total = employees.length;
  const hasAnyLocation = total > missing;
  const [strategy, setStrategy] = useState('skip'); // 'skip' | 'apply_city'
  const [cityInput, setCityInput] = useState('');

  if (missing === 0) return null;

  const apply = () => {
    if (strategy === 'apply_city') {
      const city = cityInput.trim();
      if (!city) return;
      const updated = employees.map((emp) => emp.city || emp.zip
        ? emp
        : { ...emp, city, locationInferred: true }
      );
      onResolve(updated);
    } else {
      // skip — local tax simply won't apply for missing-location employees
      onSkip();
    }
  };

  return (
    <div data-testid="smart-upload-location-prompt" style={{
      background: C.purpleLight, border: `1px solid ${C.purple}`, borderRadius: 8, padding: 16, marginBottom: 16,
    }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
        <Icon name="location-outline" style={{ fontSize: 22, color: C.purple }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
            Employee city/ZIP missing for {missing} of {total} employees
          </div>
          <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>
            City and ZIP are optional — they enable jurisdiction-aware accuracy for places like NYC (+3.08–3.88%), Philadelphia (+3.75%), Detroit (+2.4%) etc. Choose how you'd like to handle this.
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, color: C.text }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="radio" name="loc-strategy" value="skip" checked={strategy === 'skip'}
            onChange={() => setStrategy('skip')} data-testid="loc-strategy-skip"
          />
          <span><strong>Skip local taxes.</strong> Calculate without city-level savings (slightly underestimates true savings).</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="radio" name="loc-strategy" value="apply_city" checked={strategy === 'apply_city'}
            onChange={() => setStrategy('apply_city')} data-testid="loc-strategy-apply-city"
          />
          <span>
            <strong>Apply this city to all missing locations:</strong>
          </span>
          <input
            data-testid="loc-strategy-city-input"
            type="text"
            placeholder="e.g. New York City"
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            onFocus={() => setStrategy('apply_city')}
            style={{ flex: 1, padding: '6px 10px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13 }}
          />
        </label>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
        {hasAnyLocation && (
          <button
            data-testid="location-prompt-skip-only"
            onClick={onSkip}
            style={{ padding: '8px 14px', background: C.card, color: C.textMid, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Continue without changes
          </button>
        )}
        <button
          data-testid="location-prompt-apply"
          onClick={apply}
          disabled={strategy === 'apply_city' && !cityInput.trim()}
          style={{
            padding: '8px 16px', background: C.purple, color: '#fff', border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 700, cursor: strategy === 'apply_city' && !cityInput.trim() ? 'not-allowed' : 'pointer',
            opacity: strategy === 'apply_city' && !cityInput.trim() ? 0.5 : 1,
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

function PayScheduleHint({ detected }) {
  if (!detected) return null;
  const niceMap = { weekly: 'Weekly', biweekly: 'Bi-Weekly', semimonthly: 'Semi-Monthly', monthly: 'Monthly', annual: 'Annual' };
  return (
    <div data-testid="smart-upload-pay-detected" style={{
      background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 16,
      fontSize: 13, color: C.textMid,
    }}>
      Pay schedule detected: <strong style={{ color: C.text }}>{niceMap[detected] || detected}</strong>. (Pay frequency only affects display — calculations are annual.)
    </div>
  );
}

function EmailWarning({ employees }) {
  const missing = employees.filter((e) => !e.email).length;
  if (missing === 0) return null;
  return (
    <div data-testid="smart-upload-missing-email" style={{
      background: C.orangeLight, border: `1px solid ${C.orange}`, borderRadius: 8, padding: 12, marginBottom: 16,
      fontSize: 13, color: C.text, display: 'flex', gap: 10,
    }}>
      <Icon name="mail-outline" style={{ fontSize: 18, color: C.orange, flexShrink: 0, marginTop: 2 }} />
      <div>
        <strong>Email addresses missing for {missing} of {employees.length} employees.</strong> These employees can be enrolled but can't be invited electronically. You'll be able to add emails inline on the Enrollment step.
      </div>
    </div>
  );
}

export default function MissingDataPrompts({
  employees: initial,
  meta,
  clientId,
  onComplete,
}) {
  const [employees, setEmployees] = useState(initial);
  const [filingDone, setFilingDone] = useState(false);
  const [stateDone, setStateDone] = useState(false);
  const [skipFiling, setSkipFiling] = useState(false);
  const [locationDone, setLocationDone] = useState(false);

  const filingMissingCount = employees.filter((e) => !e.filing).length;
  const stateMissingCount = employees.filter((e) => !e.state).length;
  const locationMissingCount = employees.filter((e) => !e.city && !e.zip).length;

  const showFilingPrompt = !filingDone && !skipFiling && filingMissingCount > 0;
  const showStatePrompt = !showFilingPrompt && !stateDone && stateMissingCount > 0 && !meta?.detectedSingleState;
  const showLocationPrompt = !showFilingPrompt && !showStatePrompt && !locationDone && locationMissingCount > 0;

  return (
    <div>
      <PayScheduleHint detected={meta?.detectedPaySchedule} />
      <EmailWarning employees={employees} />
      {showFilingPrompt && (
        <FilingStatusPrompt
          employees={employees}
          clientId={clientId}
          onResolve={(updated) => {
            setEmployees(updated);
            setFilingDone(true);
          }}
          onSkip={() => setSkipFiling(true)}
        />
      )}
      {showStatePrompt && (
        <StatePrompt
          employees={employees}
          onResolve={(updated) => {
            setEmployees(updated);
            setStateDone(true);
          }}
        />
      )}
      {showLocationPrompt && (
        <LocationPrompt
          employees={employees}
          onResolve={(updated) => {
            setEmployees(updated);
            setLocationDone(true);
          }}
          onSkip={() => setLocationDone(true)}
        />
      )}
      {!showFilingPrompt && !showStatePrompt && !showLocationPrompt && (
        <ContinueButton onContinue={() => onComplete(employees)} />
      )}
    </div>
  );
}

function ContinueButton({ onContinue }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <button
        data-testid="smart-upload-missing-continue"
        onClick={onContinue}
        style={{
          padding: '12px 24px', background: C.green, color: '#fff',
          border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}
      >
        <Icon name="checkmark-circle" style={{ fontSize: 18 }} />
        Continue to validation
      </button>
    </div>
  );
}
